import useCustomMutation from "@/composables/useCustomMutation";
import { MutationParams } from "@/interfaces/mutationParams";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { computed, isRef, Ref, unref } from "vue";

import { useToast } from "vue-toastification";

interface Params {
  user_id: number | string | Ref<number | string>;
}
interface Props {
  params: Params;
  immediate?: boolean | Ref<boolean>;
}

interface CreateParams {
  user_id: number | string;
  cardId: string;
  deviceId: string;
  userId?: string;
  alias?: string;
}

const getCards = async (userId?: number | string) => {
  if (!userId) return;
  const { data } = await axios.get(`api/users/${userId}/cards`);
  return data;
};

const createCard = async (params?: CreateParams) => {
  if (!params) return;
  const { data } = await axios.post(
    `api/users/${params.user_id}/cards`,
    params,
  );
  return data;
};

const useUserOpenPayCard = (props?: Props) => {
  const { params, immediate = true } = props || {};
  const toast = useToast();

  const userId = computed(() => {
    if (!params) return undefined;
    return isRef(params.user_id) ? params.user_id.value : params.user_id;
  });

  const { data, isLoading, refetch, isFetching, isError } = useQuery({
    queryKey: computed(() => ["userCards", userId.value]),
    queryFn: () => getCards(userId.value),
    enabled: computed(() => !!userId.value && unref(immediate)),
    retry: false,
  });

  const createMutation = useMutation(
    (MutationParams: MutationParams<CreateParams>) =>
      createCard(MutationParams.params),
    useCustomMutation(toast, "create"),
  );

  const cards = computed(() => data.value ?? [])

  return {
    cards,
    queryCards: data,
    isLoading,
    refetch,
    isFetching,
    isError,

    createCardMutation: createMutation,
    createCard: createMutation.mutate,
    createCardAsync: createMutation.mutateAsync,
    createCardLoading: createMutation.isLoading,
  };
};

export default useUserOpenPayCard;
